import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from '../common/GuideLayout';
import GuideCta from '../common/GuideCta';
import RelatedGuides from '../common/RelatedGuides';
import { RSVP_CLUSTER } from '../common/rsvpCluster';
import { SITE_SIGNUP_URL } from '../signup/startIntent';
import { EARLIER, FAQ, MUST_HAVE, READING_MINUTES, TIMELINE, TOC, UPDATED } from './content';

// Page « Quand envoyer les faire-part de mariage » : la réponse d'abord, le
// calendrier complet, les save the date, les cas particuliers, le contenu.
const InvitationTimingGuidePage: React.FC = () => (
  <GuideLayout
    eyebrow="Guide · Faire-part"
    title="Quand envoyer les faire-part de mariage ?"
    updated={UPDATED}
    published="2026-10-15"
    readingMinutes={READING_MINUTES}
    lead="Trop tôt, vos invités l’oublient ; trop tard, ils ont déjà d’autres projets. Le bon moment pour envoyer les save the date et les faire-part, le calendrier complet jusqu’au jour J, et les cas où il vaut mieux s’y prendre plus tôt."
    topCta={
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Un faire-part qui récolte les réponses"
        text="Votre site d’invitation et son QR code : vos invités répondent en ligne, et vous voyez qui vient, au fil des réponses."
        label="Créer mon site d’invitation gratuit"
      />
    }
    toc={TOC}
    faq={FAQ}
  >
    <section id="reponse">
      <h2>La réponse courte</h2>
      <p className="mg-answer">
        Envoyez les faire-part <b>4 à 6 mois avant le mariage</b>, avec une date limite de réponse{' '}
        <b>4 à 6 semaines avant le jour J</b>. Les save the date partent plus tôt, <b>6 à 10 mois avant</b>.
      </p>
      <p>
        Six mois laissent à vos invités le temps de s’organiser (congés, garde des enfants, voyage) sans que
        l’invitation soit oubliée. En dessous de trois mois, une partie d’entre eux aura déjà d’autres projets, surtout
        l’été.
      </p>
    </section>

    <section id="calendrier">
      <h2>Le calendrier complet, du save the date au traiteur</h2>
      <div className="mg-table-scroll">
        <table className="mg-table mg-compare">
          <thead>
            <tr>
              <th scope="col">Quand</th>
              <th scope="col">Quoi</th>
              <th scope="col">Pourquoi</th>
            </tr>
          </thead>
          <tbody>
            {TIMELINE.map(([when, what, why]) => (
              <tr key={what}>
                <th scope="row">{when}</th>
                <td>
                  <b>{what}</b>
                </td>
                <td>{why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Toutes les autres étapes, mois par mois, sont dans notre{' '}
        <Link to="/retroplanning-mariage">rétroplanning de mariage</Link>.
      </p>
    </section>

    <section id="save-the-date">
      <h2>Quand envoyer les save the date ?</h2>
      <p>
        Le save the date annonce la date avant l’invitation officielle : envoyez-le <b>6 à 10 mois avant</b>, dès que la
        date et le lieu sont confirmés. Il n’est pas obligatoire, mais devient presque indispensable pour un mariage en
        pleine saison, à l’étranger, ou avec beaucoup d’invités qui viennent de loin.
      </p>
      <p>
        Une carte, un email ou un message suffisent. Pensez à y mettre l’adresse de votre site : vos invités y
        trouveront les informations au fur et à mesure.
      </p>
    </section>

    <section id="cas">
      <h2>Les cas où il faut s’y prendre plus tôt</h2>
      <div className="mg-grid">
        {EARLIER.map((e) => (
          <div key={e.title} className="mg-card">
            <h3>{e.title}</h3>
            <p>{e.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="contenu">
      <h2>Ce qu’il faut mettre sur le faire-part</h2>
      <ul className="mg-bullets">
        {MUST_HAVE.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
      <p>
        Pour la réponse, un <Link to="/qr-code-faire-part-mariage">QR code sur le faire-part</Link> remplace le carton
        réponse et le timbre : vos invités répondent en un scan, et vous n’avez plus rien à recopier.
      </p>
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Votre QR code pour le faire-part"
        text="Créez votre page d’invitation et téléchargez son QR code, prêt à imprimer. Il n’expire jamais."
        label="Créer ma page et mon QR code"
      />
    </section>

    <RelatedGuides items={[RSVP_CLUSTER.rsvp, RSVP_CLUSTER.qr, RSVP_CLUSTER.form]} />
  </GuideLayout>
);

export default InvitationTimingGuidePage;
